import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import { useImageUpload } from '../../hooks/useImageUpload'
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  Input,
  FileUpload,
  Banner,
  Animate,
  Avatar,
  Badge,
  CopyField,
  Modal,
  Tabs,
  TabPanels,
  TabPanel
} from 'wss3-forge'
import {
  Save20Regular,
  Delete20Regular,
  Building20Regular,
  Warning20Regular,
  Info20Regular
} from '@fluentui/react-icons'

export function OrgSettings() {
  const navigate = useNavigate()
  const { organization, reloadOrganization } = useOrg()
  const { uploading, uploadImage, deleteImage } = useImageUpload()

  const [activeTab, setActiveTab] = useState('general')
  const [name, setName] = useState(organization.name)
  const [logoUrl, setLogoUrl] = useState(organization.logo_url || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Delete organization state
  const [isOwner, setIsOwner] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    checkIsOwner()
  }, [organization.id])

  const checkIsOwner = async () => {
    const { data } = await supabase.rpc('is_org_owner', {
      p_organization_id: organization.id
    })
    setIsOwner(data === true)
  }

  const handleDeleteOrganization = async () => {
    if (deleteConfirmName !== organization.name) return

    setDeleting(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('organizations')
        .delete()
        .eq('id', organization.id)

      if (deleteError) throw deleteError

      navigate('/orgs')
    } catch (err: any) {
      setError(err.message || 'Failed to delete organization')
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: name.trim(),
          logo_url: logoUrl || null
        })
        .eq('id', organization.id)

      if (error) throw error

      setSuccess('Settings saved')
      await reloadOrganization()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (files: File[]) => {
    if (files.length === 0) return

    const url = await uploadImage(files[0])
    if (url) {
      setLogoUrl(url)
    }
  }

  const handleLogoDelete = async () => {
    if (logoUrl) {
      await deleteImage(logoUrl)
      setLogoUrl('')
    }
  }

  const portalUrl = `${window.location.origin}/org/${organization.slug}`

  const tabs = [
    { id: 'general', label: 'General', icon: <Building20Regular /> },
    { id: 'portal', label: 'Portal', icon: <Info20Regular /> },
    ...(isOwner ? [{ id: 'danger', label: 'Danger Zone', icon: <Warning20Regular /> }] : [])
  ]

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Settings"
          subtitle="Configure your organization's information."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {success && (
        <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <Animate type="slideInUp" delay={50}>
        <Tabs
          tabs={tabs}
          active={activeTab}
          onChange={setActiveTab}
          stretchLine
        />
      </Animate>

      <TabPanels active={activeTab}>
        <TabPanel id="general" active={activeTab}>
          <VStack gap="lg">
            <Card padding="lg">
              <VStack gap="lg">
                <Input
                  label="Organization Name"
                  value={name}
                  onChange={(v) => setName(v as string)}
                  placeholder="Your company name"
                />

                <VStack gap="xs">
                  <CopyField
                    label="Identifier"
                    value={organization.slug}
                  />
                  <Text size="xs" color="muted">
                    The identifier cannot be modified.
                  </Text>
                </VStack>

                <div>
                  <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                    Logo
                  </Text>

                  {logoUrl ? (
                    <Card variant="outlined" padding="md">
                      <HStack gap="md" style={{ alignItems: 'center' }}>
                        <Avatar src={logoUrl} name={organization.name} size="lg" />
                        <VStack gap="sm" style={{ flex: 1 }}>
                          <Text size="sm" color="muted">Current logo</Text>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={<Delete20Regular />}
                            onClick={handleLogoDelete}
                          >
                            Delete
                          </Button>
                        </VStack>
                      </HStack>
                    </Card>
                  ) : (
                    <FileUpload
                      onFilesSelected={handleLogoUpload}
                      accept="image/*"
                      maxSize={5 * 1024 * 1024}
                    />
                  )}

                  {uploading && (
                    <Text size="sm" color="muted" style={{ marginTop: '0.5rem' }}>
                      Uploading...
                    </Text>
                  )}
                </div>

                <HStack gap="md" style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
                  <Button
                    icon={<Save20Regular />}
                    loading={saving}
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                </HStack>
              </VStack>
            </Card>
          </VStack>
        </TabPanel>

        <TabPanel id="portal" active={activeTab}>
          <Card padding="lg">
            <VStack gap="lg">
              <CopyField
                label="Portal URL"
                value={portalUrl}
              />
              <HStack gap="md" style={{ alignItems: 'center' }}>
                <Text size="sm" weight="medium">Status</Text>
                <Badge variant={organization.is_active ? 'success' : 'default'}>
                  {organization.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
            </VStack>
          </Card>
        </TabPanel>

        <TabPanel id="danger" active={activeTab}>
          <VStack gap="lg">
            <Banner variant="error" title="Danger Zone">
              Actions in this section are irreversible. Proceed with caution.
            </Banner>

            <Card padding="lg">
              <VStack gap="lg">
                <VStack gap="sm">
                  <Text weight="semibold">Delete this organization</Text>
                  <Text size="sm" color="muted">
                    Once deleted, all data associated with this organization will be permanently removed.
                    This includes all members, domains, SSO connections, apps, and audit logs.
                  </Text>
                </VStack>
                <div>
                  <Button
                    variant="danger"
                    icon={<Delete20Regular />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete Organization
                  </Button>
                </div>
              </VStack>
            </Card>
          </VStack>
        </TabPanel>
      </TabPanels>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setDeleteConfirmName('')
        }}
        title="Delete Organization"
      >
        <VStack gap="lg">
          <Text>
            This action is irreversible. All organization data will be permanently deleted, including:
          </Text>
          <VStack gap="xs" style={{ paddingLeft: '1rem' }}>
            <Text size="sm" color="muted">- All members and invitations</Text>
            <Text size="sm" color="muted">- All verified domains</Text>
            <Text size="sm" color="muted">- All SSO connections</Text>
            <Text size="sm" color="muted">- All app associations</Text>
            <Text size="sm" color="muted">- All audit logs</Text>
          </VStack>
          <div>
            <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
              Type <strong>{organization.name}</strong> to confirm:
            </Text>
            <Input
              value={deleteConfirmName}
              onChange={(v) => setDeleteConfirmName(v as string)}
              placeholder={organization.name}
            />
          </div>
          <HStack gap="md" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false)
                setDeleteConfirmName('')
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteOrganization}
              loading={deleting}
              disabled={deleteConfirmName !== organization.name}
            >
              Delete
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </VStack>
  )
}
